import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import _get from 'hs-lodash/get';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { PROPERTY_NAME_TO_REFERENCE_TYPE } from '../../constants/associations';
import { PROPERTIES_LIMIT } from '../../constants/properties';
import { API_RETRY_TIMEOUT } from '../../constants/timeouts';
import { pluralTypeToId, pluralTypeToSingle } from '../../constants/types';
import { AppContext } from '../../contexts/app/context';
import { AssociationsPropsContext } from '../../contexts/associations/PropsContext';
import { AssociationsContext } from '../../contexts/associations/context';
import { PropertiesContext } from '../../contexts/properties/context';
import EditHOC from '../../hoc/EditHOC';
import FeaturePopupOver from '../common/FeaturePopupOver';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_AMOUNT_LOAD_OBJECT_ENTITIES_ATTEMPTS = 30;
function AssociationSection({
  objectType,
  children,
  disabled,
  feature,
  openAssociationModal
}) {
  const {
    usageTracker,
    portalId,
    openUrl,
    featureTipsLink,
    reconnectRoute
  } = useContext(AssociationsPropsContext);
  const {
    associations,
    associationsActions,
    associatedObjects
  } = useContext(AssociationsContext);
  const {
    searchProperties,
    properties,
    arePropertiesLoaded,
    propertiesActions,
    externalOptions,
    mentionOwners
  } = useContext(PropertiesContext);
  const {
    isPastMeeting,
    appName,
    features
  } = useContext(AppContext);
  const associationCount = useMemo(() => associations[objectType] ? (associations[objectType] || []).length : 0, [associations, objectType]);
  const associationCountRef = useRef(associationCount);
  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const isExternalOptionFetched = useRef(false);
  const [areObjectEntitiesLoading, setAreObjectEntitiesLoading] = useState(false);
  const [objectEntititesLoadAttempt, setObjectEntititesLoadAttempt] = useState(0);
  const onClickAdd = () => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS.associations,
      type: pluralTypeToSingle[objectType]
    }, ACTIONS.associations.clickAdd));
    associationsActions.setCrmObjectType(pluralTypeToSingle[objectType]);
    openAssociationModal();
  };
  const getCallObjectAssociations = useCallback((options = {}) => {
    associationsActions.getAssociatedObjects(Object.assign({
      objectType,
      associationLoading: true,
      properties,
      associatedObjects,
      associations
    }, options));
  }, [objectType, properties, associationsActions, associations, associatedObjects]);
  const onOpenChangeHandler = event => {
    setIsSectionOpen(event.target.value);
    if (event.target.value && !arePropertiesLoaded[pluralTypeToSingle[objectType]] && associationCount) {
      propertiesActions.getProperties({
        featuresStatus: features,
        objectType: pluralTypeToSingle[objectType]
      });
    }
  };
  useEffect(() => {
    if (associationCountRef.current !== associationCount && !arePropertiesLoaded[pluralTypeToSingle[objectType]]) {
      associationCountRef.current = associationCount;
      propertiesActions.getProperties({
        featuresStatus: features,
        objectType: pluralTypeToSingle[objectType]
      });
    }
  }, [arePropertiesLoaded, associationCount, propertiesActions, features, objectType]);

  // keep track of the number of associations and load new entities if needed
  useEffect(() => {
    const areAssociationObjectsLoaded = (associations[objectType] || []).every(association => !!associatedObjects[objectType][association.id]);
    if (!areAssociationObjectsLoaded && isSectionOpen && !areObjectEntitiesLoading && objectEntititesLoadAttempt < MAX_AMOUNT_LOAD_OBJECT_ENTITIES_ATTEMPTS) {
      setAreObjectEntitiesLoading(true);
      setObjectEntititesLoadAttempt(objectEntititesLoadAttempt + 1);
      getCallObjectAssociations({
        associationLoading: objectEntititesLoadAttempt === 0,
        isPostponedAction: false,
        cb: () => setTimeout(() => setAreObjectEntitiesLoading(false), API_RETRY_TIMEOUT * 2)
      });
    } else if (areAssociationObjectsLoaded) {
      setObjectEntititesLoadAttempt(0);
    }
  }, [associations, objectType, getCallObjectAssociations, isSectionOpen, associatedObjects, areObjectEntitiesLoading, objectEntititesLoadAttempt]);

  // fetch external options for association cards, to show labels instead of values
  useEffect(() => {
    if (associationCount) {
      const objectCardProperties = properties[pluralTypeToSingle[objectType]].associationCardProperties;
      const objectProperties = searchProperties[pluralTypeToSingle[objectType]].filter(item => objectCardProperties.includes(item.name) && item.externalOptions);
      if (!isExternalOptionFetched.current && objectProperties.length) {
        // need to prevent infinite loop
        isExternalOptionFetched.current = true;
        objectProperties.forEach(property => {
          const hardcodedRefType = _get(PROPERTY_NAME_TO_REFERENCE_TYPE[pluralTypeToSingle[objectType]], property.name, '');
          propertiesActions.getExternalOptions({
            query: '',
            referenceType: property.referenceType || property.referencedObjectType || hardcodedRefType || '',
            offset: 0,
            limit: PROPERTIES_LIMIT,
            typeId: pluralTypeToId[objectType],
            crmObjectType: pluralTypeToSingle[objectType],
            externalOptions,
            mentionOwners
          });
        });
      }
    }
  }, [properties, objectType, searchProperties, associationCount, propertiesActions, externalOptions, mentionOwners]);
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(ExpandableSection, {
      onOpenChange: onOpenChangeHandler,
      "data-test-id": `${appName}-associations-section-${objectType}`,
      indentation: "compact",
      defaultOpen: false,
      "data-loaded": arePropertiesLoaded[pluralTypeToSingle[objectType]],
      disabled: disabled,
      actions: /*#__PURE__*/_jsxs(_Fragment, {
        children: [!feature.available ? /*#__PURE__*/_jsx(FeaturePopupOver, {
          featureDisabledReason: feature.reason,
          portalId: portalId,
          openUrl: openUrl,
          featureTipsLink: featureTipsLink,
          reconnectRoute: reconnectRoute
        }) : null, /*#__PURE__*/_jsx(EditHOC, {
          isPastMeeting: isPastMeeting,
          children: /*#__PURE__*/_jsx(Button, {
            disabled: disabled,
            "data-test-id": `${appName}-associations-add-button-${objectType}`,
            use: "transparent",
            onClick: onClickAdd,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "associationModals.add"
            })
          })
        })]
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `associationList.${objectType}`,
        options: {
          count: associationCount
        }
      }),
      children: children
    })
  });
}
export default AssociationSection;